import type { CompanyLookupResponse } from '~/types/address'
import { apiErrorStatus, clientInvoiceApiErrors, companyFromLookup, isValidEik, normalizeEik, type CompanyView } from '~/utils/client-invoice'

/**
 * EIK lookup through the API: GET /security/company-lookup/{eik} for a logged-in user with a linked client,
 * GET /Public/company-lookup/{eik} for everybody else (`isPublic`).
 * `errorCode` uses the API codes: invalid, not_found, lookup_unavailable, too_many_lookups.
 */
export function useCompanyLookup(isPublic: MaybeRefOrGetter<boolean> = false) {
  const api = useApi()
  const company = ref<CompanyView | null>(null)
  const loading = ref(false)
  const errorCode = ref('')
  let timer: ReturnType<typeof setTimeout> | null = null
  let seq = 0

  function reset() {
    if (timer) clearTimeout(timer)
    seq++
    company.value = null
    loading.value = false
    errorCode.value = ''
  }

  async function run(eik: string) {
    const id = ++seq
    loading.value = true
    errorCode.value = ''
    try {
      const scope = toValue(isPublic) ? 'Public' : 'security'
      const data = await api<CompanyLookupResponse>(`/${scope}/company-lookup/${eik}`)
      if (id === seq) company.value = companyFromLookup(data)
    }
    catch (error) {
      if (id !== seq) return
      company.value = null
      const code = clientInvoiceApiErrors(error, 0, 0).eik
      errorCode.value = code || (apiErrorStatus(error) === 429 ? 'too_many_lookups' : 'lookup_unavailable')
    }
    finally {
      if (id === seq) loading.value = false
    }
  }

  /** Debounced (600 ms) unless immediate; an invalid non-empty value gets `invalid` after the same pause. */
  function lookup(raw: string, options?: { immediate?: boolean }) {
    reset()
    const eik = normalizeEik(raw)
    if (!eik) return
    if (options?.immediate) {
      validateNow(eik)
      return
    }
    timer = setTimeout(() => validateNow(eik), 600)
  }

  /** On blur: a pending lookup or the `invalid` code starts at once. */
  function validateNow(raw: string) {
    const eik = normalizeEik(raw)
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    if (!eik) return
    if (!isValidEik(eik)) {
      seq++
      company.value = null
      loading.value = false
      errorCode.value = 'invalid'
      return
    }
    if (loading.value || company.value || errorCode.value) return
    void run(eik)
  }

  onScopeDispose(reset)

  return { company, loading, errorCode, lookup, validateNow, reset }
}
